import {areIntervalsOverlapping, isSameDay} from 'date-fns';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';

/**
 * Checks if two events should be treated as overlapping for collision detection.
 * Events that occur on the same day are always considered overlapping to force
 * them into separate rows, even if they don't overlap in time.
 *
 * @param eventA - First event
 * @param eventB - Second event
 * @returns True if events overlap or are on the same day, false otherwise
 *
 * @example
 * const event1 = { start: new Date('2024-01-01 09:00'), end: new Date('2024-01-01 11:00') };
 * const event2 = { start: new Date('2024-01-01 13:00'), end: new Date('2024-01-01 15:00') };
 *
 * eventsOverlap(event1, event2); // true (same day, different times)
 */
export function eventsOverlap(
  eventA: RentalResourceCalendarEvent,
  eventB: RentalResourceCalendarEvent
): boolean {
  // Check if events are on the same day (start or end dates)
  const sameDay =
    isSameDay(eventA.start, eventB.start) ||
    isSameDay(eventA.start, eventB.end) ||
    isSameDay(eventA.end, eventB.start) ||
    isSameDay(eventA.end, eventB.end);

  if (sameDay) {
    return true;
  }

  // For events spanning multiple days, check actual time overlap
  return areIntervalsOverlapping(
    {start: eventA.start, end: eventA.end},
    {start: eventB.start, end: eventB.end}
  );
}
